import { t } from '@procumi/shared';
import { useState, type ReactNode } from 'react';
import { I, Section } from './shared';

export interface AttentionItem {
  key: string;
  title: string;
  detail: string;
  /** butonul pasului următor pe rândul din listă */
  action?: ReactNode;
  open: () => void;
}

export interface AttentionGroup {
  key: string;
  urgency: 'urgent' | 'action';
  /** roșu = se pierd bani sau timp acum; portocaliu / galben = de făcut; albastru, gri = pasul următor */
  tone: 'red' | 'orange' | 'yellow' | 'blue' | 'gray';
  /** „2 răspunsuri întârziate” */
  title: (n: number) => string;
  /** butonul grupului: „Follow-up”, „Compară”, „Aprobă”… acționează pe primul */
  cta: string;
  run: () => void;
  items: AttentionItem[];
}

/**
 * „Necesită atenție” ca o căsuță de lucru: întâi ce e urgent, apoi ce e de făcut.
 * Un rând pe fel de lucru, cu primul caz scris sub titlu și butonul care îl
 * rezolvă; celelalte cazuri se deschid sub rând.
 */
export function AttentionPanel({ groups }: { groups: AttentionGroup[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const live = groups.filter((g) => g.items.length);
  const block = (u: AttentionGroup['urgency'], label: string) => {
    const rows = live.filter((g) => g.urgency === u);
    if (!rows.length) return null;
    return (
      <div className="db-inbox-b">
        <span className={`db-inbox-k ${u}`}>{label}</span>
        <ul>
          {rows.map((g) => {
            const first = g.items[0]!;
            const on = open === g.key;
            return (
              <li key={g.key} className={on ? 'on' : undefined}>
                <div className="db-inbox-r">
                  <span className={`db-dot ${g.tone}`} />
                  <button type="button" className="db-inbox-m" onClick={first.open}>
                    <b>{t(g.title(g.items.length))}</b>
                    <span>
                      {t(first.title)} · {t(first.detail)}
                    </span>
                  </button>
                  {g.items.length > 1 ? (
                    <button
                      type="button"
                      className="db-inbox-more"
                      aria-expanded={on}
                      onClick={() => setOpen(on ? null : g.key)}
                      title={on ? t('Ascunde') : t('Toate {0}', g.items.length)}
                    >
                      +{g.items.length - 1}
                      <I d="down" size={12} />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={`btn small${g.tone === 'red' ? '' : ' primary'}`}
                    onClick={g.run}
                  >
                    {g.cta}
                  </button>
                </div>
                {on ? (
                  <ul className="db-inbox-l">
                    {g.items.slice(1, 8).map((it) => (
                      <li key={it.key}>
                        <button type="button" className="db-inbox-m" onClick={it.open}>
                          <b>{t(it.title)}</b>
                          <span>{t(it.detail)}</span>
                        </button>
                        {it.action}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };
  const total = live.reduce((n, g) => n + g.items.length, 0);
  return (
    <Section
      icon="alert"
      tone="red"
      title={t('Necesită atenție')}
      sub={
        total
          ? t('Lucrurile din achiziții care așteaptă un pas de la tine')
          : t('Nimic nu așteaptă după tine acum')
      }
      className="db-inbox"
      id="db-att"
    >
      {live.length ? (
        <>
          {block('urgent', t('Urgent'))}
          {block('action', t('De făcut'))}
        </>
      ) : (
        <div className="db-empty">
          <span className="db-ok">
            <I d="check" />
          </span>
          {t(
            'Totul e la zi: nicio ofertă de comparat, niciun furnizor care întârzie, nicio livrare restantă.',
          )}
        </div>
      )}
    </Section>
  );
}
